import { useState } from 'react';
import {
  Spin,
  Input,
  Button,
  Tag,
  Modal,
  Empty,
  message,
  Table, // Import Table from antd
  Tooltip,
  Typography,
} from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useDebounce from '../../utils/debounce';
import {
  useDeleteChargingStation,
  useFetchChargingStations,
  useBlockLocation,
} from './Hooks';
import { useOperatorStore } from '../../store/useOperatorStore';
import LocationFormModal from '../../components/Modals/LocationFormModal';
import { useQueryClient } from '@tanstack/react-query';
import LocationSingleDrawer from './[id]/LocationSingleDrawer';
import { useAuthStore } from '../../store/useAuthStore';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import { SearchOutlined } from '@ant-design/icons';
import WarningMessage from '../../components/WarningMessage';

const { Title } = Typography;

const LocationsPage = () => {
  const { t } = useTranslation('location'); // i18n support
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isStationFormOpen, setIsStationFormOpen] = useState(false);
  const { operatorId } = useOperatorStore();
  const queryClient = useQueryClient();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editStation, setEditStation] = useState(null);
  const { user } = useAuthStore();
  const isEditAllowed = useIsEditAllowedWithOperator();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stationToDelete, setStationToDelete] = useState(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [locationToBlock, setLocationToBlock] = useState(null);
  const [blockAction, setBlockAction] = useState('BLOCK');

  const { data, isLoading } = useFetchChargingStations({
    operatorId,
    offset: page,
    limit: pageSize,
    search: debouncedSearch,
  });

  const deleteStation = useDeleteChargingStation(operatorId, {
    onSuccess: () => {
      message.success(t('messages.deleteSuccess'));
      queryClient.invalidateQueries(['listStations']);
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || error?.response?.data?.status_message
      );
    },
  });

  const blockLocation = useBlockLocation({
    onSuccess: () => {
      message.success(
        blockAction === 'BLOCK'
          ? t('blockSuccessMessage')
          : t('unblockSuccessMessage')
      );
      queryClient.invalidateQueries(['listStations']);
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || error?.response?.data?.status_message
      );
    },
  });

  const totalItems = data?.totalCount || 0;
  const stations = data?.paginatedData || [];

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    if (page !== 1) setPage(1);
  };

  const handlePageChange = (currentPage) => {
    setPage(currentPage);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries(['listStations']);
  };

  const confirmDeleteStation = () => {
    deleteStation.mutateAsync({ id: stationToDelete?._id });
    setDeleteModalOpen(false);
  };

  const showDeleteConfirm = (stationId) => {
    setStationToDelete(stations.find((station) => station._id === stationId));
    setDeleteModalOpen(true);
  };

  const showBlockConfirm = (locationId) => {
    const location = stations.find((station) => station._id === locationId);
    setLocationToBlock(location);
    setBlockAction(location?.status === 'BLOCKED' ? 'UNBLOCK' : 'BLOCK');
    setBlockModalOpen(true);
  };

  const confirmBlockLocation = () => {
    if (locationToBlock) {
      const status = blockAction === 'BLOCK' ? 'BLOCKED' : 'ACTIVE';
      blockLocation.mutateAsync({ id: locationToBlock._id, status });
    }
    setBlockModalOpen(false);
  };

  const getBlockModalTitle = () =>
    (
      <Title level={4} className="mb-0!">
        {blockAction === 'BLOCK' ? t('blockModalTitle') : t('unblockModalTitle')}
      </Title>
    );
  const getBlockModalMessage = () =>
    blockAction === 'BLOCK' ? t('blockModalMessage') : t('unblockModalMessage');
  const getDeleteSubMessage = () => t('deleteSubMessage');
  const columns = [
    {
      title: t('table.name'),
      dataIndex: 'locationName',
      key: 'locationName',
      align: 'center',
      render: (text, record) => (
        <span
          className="whitespace-nowrap capitalize cursor-pointer hover:text-[#28A745]"
          onClick={() => setSelectedLocation(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      align: 'center',
      key: 'status',
      render: (text) => (
        <Tag color={text === 'ACTIVE' ? 'green' : 'red'} variant="outlined">
          {text === 'ACTIVE' ? t('status.active') : t('status.blocked')}
        </Tag>
      ),
    },
    {
      title: t('table.action'),
      key: 'action',
      align: 'center',
      render: (record) => (
        <div className="flex flex-row gap-1 justify-center">
          <div>
            <Tooltip title={t('buttons.view')}>
              <Button
                type="default"
                icon={<EyeOutlined />}
                onClick={() => setSelectedLocation(record)}
              />
            </Tooltip>
          </div>
          {isEditAllowed && (
            <div className="flex flex-row gap-2">
              <div>
                <Tooltip title={t('buttons.edit')}>
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    disabled={isReadOnlyUser}
                    onClick={() => {
                      setEditStation(record);
                      setIsStationFormOpen(true);
                    }}
                  />
                </Tooltip>
              </div>
              <div>
                <Tooltip
                  title={
                    record.status === 'BLOCKED'
                      ? t('unblock') || 'Unblock'
                      : t('block')
                  }
                >
                  <Button
                    type="default"
                    icon={
                      record.status === 'BLOCKED' ? <UnlockOutlined /> : <LockOutlined />
                    }
                    disabled={isReadOnlyUser}
                    onClick={() => showBlockConfirm(record._id)}
                    loading={blockLocation.isLoading}
                  />
                </Tooltip>
              </div>
              <div>
                <Tooltip title={t('buttons.delete')}>
                  <Button
                    type="default"
                    danger
                    disabled={isReadOnlyUser}
                    icon={<DeleteOutlined />}
                    onClick={() => showDeleteConfirm(record._id)}
                  />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {!operatorId ? (
        <WarningMessage />
      ) : (
        <>
          <div className="flex justify-between items-center mb-4 gap-4">
            <div className="flex items-center gap-2">
              <Input
                allowClear
                style={{ width: 300 }}
                placeholder={t('placeholders.search')}
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <Tooltip title={t('buttons.refresh')}>
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={isLoading}
                />
              </Tooltip>
            </div>
            {isEditAllowed && (
              <div className="flex justify-end flex-1">
                <Button
                  type="primary"
                  disabled={isReadOnlyUser}
                  onClick={() => setIsStationFormOpen(true)}
                >
                  {t('buttons.addLocation')}
                </Button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center my-8">
              <Spin size="small" />
            </div>
          ) : stations.length > 0 ? (
            <Table
              columns={columns}
              dataSource={stations}
              variant="outlined"
              rowKey="_id"
              size="small"
              tableLayout='fixed'
              pagination={{
                current: page,
                pageSize: pageSize,
                position: ['bottomCenter'],
                showSizeChanger: false,
                size: 'default',
                total: totalItems,
                onChange: handlePageChange,
              }}
            />
          ) : (
            <Empty description={t('empty.noStations')} />
          )}

          {selectedLocation && (
            <LocationSingleDrawer
              isOpen={selectedLocation}
              onClose={() => {
                setSelectedLocation(null);
                setSelectedLocation(null);
              }}
              selectedLocation={selectedLocation}
            />
          )}

          {isStationFormOpen && (
            <LocationFormModal
              isOpen={isStationFormOpen}
              onClose={() => {
                setIsStationFormOpen(false);
                setEditStation(false);
              }}
              editLocation={editStation}
            />
          )}
          <Modal
            confirmLoading={deleteStation.isLoading}
            title={<Title level={4} className="mb-0!">{t('modal.deleteTitle')}</Title>}
            open={deleteModalOpen}
            onCancel={() => setDeleteModalOpen(false)}
            onOk={confirmDeleteStation}
          >
            <p>{t('modal.deleteMessage')}</p>
            <p className='mt-2 text-orange-500'>{getDeleteSubMessage()}</p>

          </Modal>
          <Modal
            confirmLoading={blockLocation.isLoading}
            title={<Title level={4} className="mb-0!">{getBlockModalTitle()}</Title>}
            open={blockModalOpen}
            onCancel={() => setBlockModalOpen(false)}
            onOk={confirmBlockLocation}
            okButtonProps={{
              loading: blockLocation.isLoading,
            }}
          >
            <p>{getBlockModalMessage()}</p>
          </Modal>
        </>
      )}
    </div>
  );
};

export default LocationsPage;
